<script setup lang="ts">
import { onBeforeMount } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import router from '@/router';

import api from '@/api';
import { updateToken } from '@/api/backend/_http';
import { CurrentKey, LoanType, FinishType } from '@/helpers/const';
import { resolveLiveBroker } from '@/helpers/brokerSession';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';

import type { RouteParamsRaw } from 'vue-router';
import type { UtmMetricDto } from '@/api/backend/users/applications';
import { type UserQuoteProcessStepEnum, UserQuoteProcessSteps } from '@/types';
import type { LoanApplication, AppUser } from '@/types/LoanApplication';

const cookies = useCookies();
const { send, service, state, restart } = useUserQuoteProcess();

const props = defineProps<{
  utmMetrics: UtmMetricDto;
  from?: string;
  email?: string;
}>();

onBeforeMount(async () => {
  // If it's a direct URL access, we store the utmMetrics.
  if (!history.state.back) {
    localStorage.setItem(CurrentKey, '');
    restart();

    try {
      const loanAppDetails = await getLoanAppDetails();

      state.value.context.loanApp = {
        ...state.value.context.loanApp,
        ...loanAppDetails,
      };
    } catch (error) {
      console.error('Failed to initialize broker quote details:', error);
    }
  }

  state.value.context.loanApp = {
    ...state.value.context.loanApp,
    ...(hasUtmValues(props.utmMetrics) && { utmMetrics: props.utmMetrics }),
    ...(props.from && { from: props.from }),
  };

  send('READY');
});

const USER_TYPE_SUFFIXES: Partial<Record<AppUser, string>> = {
  Broker: 'broker',
  UnaccreditedBroker: 'broker',
  Crm: 'crm_user',
} as const;

const getLoanAppDetails = async () => {
  const { from } = props;
  const brokerToken = cookies.get('broker-token');
  const crmToken = cookies.get('crm-token');

  if (crmToken && from === 'crm') {
    updateToken(crmToken);

    const {
      data: { crmUser },
    } = await api.backend.crm.user.currentUser();

    if (!crmUser) return null;

    const { id, email, firstName, lastName } = crmUser;

    return {
      userType: 'Crm' as const,
      crmId: id,
      crmEmail: email,
      crmFullName: [firstName, lastName].filter(Boolean).join(' '),
    };
  }

  if (brokerToken) {
    updateToken(brokerToken);

    // `broker-token` is a session cookie shared across *.bridgit.com.au and outlives
    // the JWT it holds, so it may no longer identify a broker. When it doesn't, the
    // stale session is cleared and the quote continues as an unaccredited broker,
    // who can re-verify by email below.
    const broker = await resolveLiveBroker();

    if (!broker) {
      return {
        userType: 'UnaccreditedBroker' as const,
        ...(props.email && { brokerEmail: props.email }),
      };
    }

    const { id, email, firstName, lastName } = broker;
    const brokerReferenceNumber = 'brokerReferenceNumber' in broker ? broker.brokerReferenceNumber ?? '' : '';

    cookies.set('broker-id', id, { path: '/' });

    return {
      userType: 'Broker' as const,
      brokerId: id,
      brokerEmail: email,
      brokerReferenceNumber,
      brokerFullName: [firstName, lastName].filter(Boolean).join(' '),
    };
  }

  return {
    userType: 'UnaccreditedBroker' as const,
    ...(props.email && { brokerEmail: props.email }),
  };
};

const hasUtmValues = (metrics: UtmMetricDto): boolean => {
  if (!metrics) return false;

  return Object.values(metrics).some((value) => value && value.trim() !== '');
};

const getFinishType = (loanApp: LoanApplication): string => {
  const { loanType, userType } = loanApp;

  const baseType = loanType === LoanType.SINGLE_SECURITY ? FinishType.SINGLE_SECURITY : FinishType.BRIDGE_LOAN;
  const suffix = userType ? USER_TYPE_SUFFIXES[userType] || '' : '';

  return suffix ? `${baseType}_${suffix}` : baseType;
};

service.onTransition((transition) => {
  if (transition.value !== 'gotoReady' && transition.value !== 'start') {
    const key = transition.value as UserQuoteProcessStepEnum;
    const name = UserQuoteProcessSteps[key].route;

    let params: RouteParamsRaw | undefined;

    if (name === 'userQuoteCompleted') {
      params = { finishType: getFinishType(transition.context.loanApp) };
    }

    router.push({ name, params });
  }
});
</script>

<template>
  <div></div>
</template>
